import type {
  DriverActionRequest,
  DriverActionResult,
  DriverParcel,
  DriverRouteToday,
  DriverStop,
  FailureReasonKey,
  ParcelStatus,
  StopStatus,
} from '../api/types';
import { parcelTail } from '../lib/parcel';

/** Ladderposities; hoger is verder gevorderd. Wordt gebruikt om nooit terug te merken. */
const STOP_RANK: Record<StopStatus, number> = {
  planned: 0,
  enRoute: 1,
  arrived: 2,
  delivered: 3,
  failed: 3,
  redeliveryPlanned: 3,
  cancelled: 4,
};

const PARCEL_RANK: Record<ParcelStatus, number> = {
  created: 0,
  labelGenerated: 1,
  readyForPickup: 2,
  pickedUp: 3,
  inTransit: 4,
  delivered: 5,
  failed: 5,
  redeliveryPlanned: 5,
  cancelled: 6,
};

/** De Nederlandse tekst die de server in `FailureReason` schrijft (contract §5.4). */
export const FAILURE_REASON_TEXT: Record<FailureReasonKey, string> = {
  noOnePresent: 'Niemand aanwezig',
  closed: 'Gesloten',
  wrongAddress: 'Adres klopt niet',
  parcelDamaged: 'Pakket beschadigd',
};

export function isStopHandled(status: StopStatus): boolean {
  return (
    status === 'delivered' ||
    status === 'failed' ||
    status === 'redeliveryPlanned' ||
    status === 'cancelled'
  );
}

export function isParcelHandled(status: ParcelStatus): boolean {
  return (
    status === 'delivered' ||
    status === 'failed' ||
    status === 'redeliveryPlanned' ||
    status === 'cancelled'
  );
}

/** Stops die de bezorger ziet; geannuleerde stops horen niet in de rit. */
export function visibleStops(snapshot: DriverRouteToday): DriverStop[] {
  return snapshot.stops.filter((stop) => stop.status !== 'cancelled');
}

/** `currentStop` = de eerste stop die nog niet afgehandeld is. */
export function currentStop(snapshot: DriverRouteToday): DriverStop | null {
  return visibleStops(snapshot).find((stop) => !isStopHandled(stop.status)) ?? null;
}

export function allHandled(snapshot: DriverRouteToday): boolean {
  const stops = visibleStops(snapshot);
  return stops.length > 0 && stops.every((stop) => isStopHandled(stop.status));
}

export interface StopCounts {
  total: number;
  delivered: number;
  /** Mislukt én met herbezorging: samen wat de bezorger als "niet gelukt" ziet. */
  failed: number;
  /** Deelverzameling van `failed` waarvoor een herbezorging gepland staat. */
  redelivered: number;
  open: number;
}

export function stopCounts(snapshot: DriverRouteToday): StopCounts {
  const stops = visibleStops(snapshot);
  let delivered = 0;
  let failed = 0;
  let redelivered = 0;
  for (const stop of stops) {
    if (stop.status === 'delivered') delivered += 1;
    else if (stop.status === 'failed' || stop.status === 'redeliveryPlanned') {
      failed += 1;
      if (stop.status === 'redeliveryPlanned') redelivered += 1;
    }
  }
  return {
    total: stops.length,
    delivered,
    failed,
    redelivered,
    open: stops.length - delivered - failed,
  };
}

export interface LoadCounts {
  total: number;
  loaded: number;
  notTaken: number;
  /** Alles is óf geladen óf bewust niet meegenomen: "Rit starten" mag. */
  complete: boolean;
}

export function loadCounts(snapshot: DriverRouteToday): LoadCounts {
  let total = 0;
  let loaded = 0;
  let notTaken = 0;
  for (const stop of visibleStops(snapshot)) {
    for (const parcel of stop.parcels) {
      total += 1;
      if (parcel.loaded) loaded += 1;
      else if (parcel.notTaken) notTaken += 1;
    }
  }
  return { total, loaded, notTaken, complete: total > 0 && loaded + notTaken === total };
}

export function allParcels(snapshot: DriverRouteToday): DriverParcel[] {
  return visibleStops(snapshot).flatMap((stop) => stop.parcels);
}

export function findStopById(snapshot: DriverRouteToday, stopId: string | null): DriverStop | null {
  if (!stopId) return null;
  return snapshot.stops.find((stop) => stop.id === stopId) ?? null;
}

function withStops(snapshot: DriverRouteToday, stops: DriverStop[]): DriverRouteToday {
  return { ...snapshot, stops };
}

function mapStop(
  snapshot: DriverRouteToday,
  stopId: string,
  fn: (stop: DriverStop) => DriverStop,
): DriverRouteToday {
  return withStops(
    snapshot,
    snapshot.stops.map((stop) => (stop.id === stopId ? fn(stop) : stop)),
  );
}

function mapParcelByNumber(
  snapshot: DriverRouteToday,
  parcelNumber: string,
  fn: (parcel: DriverParcel, stop: DriverStop) => DriverParcel,
  afterStop?: (stop: DriverStop) => DriverStop,
): DriverRouteToday {
  const tail = parcelTail(parcelNumber);
  return withStops(
    snapshot,
    snapshot.stops.map((stop) => {
      if (!stop.parcels.some((p) => parcelTail(p.parcelNumber) === tail)) return stop;
      const parcels = stop.parcels.map((parcel) =>
        parcelTail(parcel.parcelNumber) === tail ? fn(parcel, stop) : parcel,
      );
      const next: DriverStop = { ...stop, parcels };
      return afterStop ? afterStop(next) : next;
    }),
  );
}

/** Zet de bezoekstatus af op de pakketten: pas afgehandeld als álle pakketten dat zijn (E4). */
function reconcileStop(stop: DriverStop, occurredAt: string): DriverStop {
  const active = stop.parcels;
  if (active.length === 0) return stop;
  const everyHandled = active.every((parcel) => isParcelHandled(parcel.status));
  if (!everyHandled) return stop;
  const everyDelivered = active.every((parcel) => parcel.status === 'delivered');
  if (everyDelivered) {
    return { ...stop, status: 'delivered', deliveredAt: stop.deliveredAt ?? occurredAt };
  }
  const anyRedelivery = active.some((parcel) => parcel.status === 'redeliveryPlanned');
  return { ...stop, status: anyRedelivery ? 'redeliveryPlanned' : 'failed' };
}

/**
 * Past een handeling onmiddellijk lokaal toe (offline-first): de bezorger ziet succes
 * zonder op de server te wachten. Dezelfde overgangen als contract §5.3.
 */
export function applyAction(
  snapshot: DriverRouteToday,
  request: DriverActionRequest,
): DriverRouteToday {
  const at = request.occurredAt;

  switch (request.type) {
    case 'route.startPickup':
      return {
        ...snapshot,
        route: {
          ...snapshot.route,
          status: snapshot.route.status === 'completed' ? snapshot.route.status : 'inProgress',
          closedAt: snapshot.route.closedAt ?? at,
        },
      };

    case 'route.start':
      return {
        ...withStops(
          snapshot,
          snapshot.stops.map((stop) => ({
            ...stop,
            status: stop.status === 'planned' ? 'enRoute' : stop.status,
            parcels: stop.parcels.map((parcel) =>
              parcel.status === 'pickedUp' ? { ...parcel, status: 'inTransit' } : parcel,
            ),
          })),
        ),
        route: { ...snapshot.route, actualStartAt: snapshot.route.actualStartAt ?? at },
      };

    case 'parcel.load':
      if (!request.parcelNumber) return snapshot;
      return mapParcelByNumber(snapshot, request.parcelNumber, (parcel) => ({
        ...parcel,
        loaded: true,
        notTaken: false,
        status: PARCEL_RANK[parcel.status] < PARCEL_RANK.pickedUp ? 'pickedUp' : parcel.status,
      }));

    case 'parcel.notTaken':
      if (!request.parcelNumber) return snapshot;
      return mapParcelByNumber(snapshot, request.parcelNumber, (parcel) => ({
        ...parcel,
        notTaken: true,
        loaded: false,
      }));

    case 'stop.arrive': {
      if (!request.stopId) return snapshot;
      return mapStop(snapshot, request.stopId, (stop) => {
        // Automaat uit contract §5.3: een niet-meegenomen pakket wordt bij aankomst mislukt.
        const parcels: DriverParcel[] = stop.parcels.map((parcel) =>
          parcel.notTaken && !isParcelHandled(parcel.status)
            ? { ...parcel, status: 'failed' }
            : parcel,
        );
        const arrived: DriverStop = {
          ...stop,
          parcels,
          status: STOP_RANK[stop.status] < STOP_RANK.arrived ? 'arrived' : stop.status,
          arrivedAt: stop.arrivedAt ?? at,
          failureReason: parcels.some((p) => p.status === 'failed' && p.notTaken)
            ? (stop.failureReason ?? 'Pakket niet meegenomen')
            : stop.failureReason,
          // A-07.2: `notTaken` is uitsluitend een uitkomst, nooit iets wat de app stuurt.
          failureCode: parcels.some((p) => p.status === 'failed' && p.notTaken)
            ? (stop.failureCode ?? 'notTaken')
            : stop.failureCode,
        };
        return reconcileStop(arrived, at);
      });
    }

    case 'stop.deliver': {
      if (!request.parcelNumber) return snapshot;
      return mapParcelByNumber(
        snapshot,
        request.parcelNumber,
        (parcel) => ({ ...parcel, status: 'delivered', loaded: true, notTaken: false }),
        (stop) => reconcileStop(stop, at),
      );
    }

    case 'stop.fail': {
      if (!request.stopId || !request.failureReason) return snapshot;
      // Amendement A-07.1: herbezorging = de schakelaar óf een meegegeven datum.
      const redelivers = request.planRedelivery === true || Boolean(request.redeliveryPlannedFor);
      const status: 'failed' | 'redeliveryPlanned' = redelivers ? 'redeliveryPlanned' : 'failed';
      const base = FAILURE_REASON_TEXT[request.failureReason];
      const reason = request.note ? `${base}: ${request.note}`.slice(0, 500) : base;
      return mapStop(snapshot, request.stopId, (stop) => {
        const targeted = request.parcelNumber ? parcelTail(request.parcelNumber) : null;
        const parcels = stop.parcels.map((parcel) => {
          if (targeted && parcelTail(parcel.parcelNumber) !== targeted) return parcel;
          if (isParcelHandled(parcel.status)) return parcel;
          return { ...parcel, status };
        });
        return {
          ...stop,
          parcels,
          status,
          failureReason: reason,
          failureCode: request.failureReason,
          invoiceAnyway: request.invoiceAnyway ?? false,
          // De server kiest de eerstvolgende bezorgdag (A-04); tot het antwoord binnen is
          // toont de app "morgenavond".
          redeliveryPlannedFor: request.redeliveryPlannedFor ?? null,
        };
      });
    }

    case 'route.complete':
      return {
        ...snapshot,
        route: {
          ...snapshot.route,
          status: 'completed',
          actualEndAt: snapshot.route.actualEndAt ?? at,
        },
      };

    default:
      return snapshot;
  }
}

/**
 * "Toch niet? Draai terug" (B6). Lokaal, en alleen zolang het item nog niet verzonden is:
 * de exacte stoptoestand van vóór de handeling komt terug.
 */
export function revertStopInSnapshot(
  snapshot: DriverRouteToday,
  previous: DriverStop,
): DriverRouteToday {
  return withStops(
    snapshot,
    snapshot.stops.map((stop) => (stop.id === previous.id ? previous : stop)),
  );
}

/**
 * Merget het antwoord van het actie-endpoint in de cache (contract §5.2): zo pikt de app
 * na een offline periode op dat een bezoek intussen mislukt is. Een status die lokaal al
 * verder is wordt nooit teruggezet — de bezorger heeft altijd het laatste woord over
 * wat hij zojuist deed.
 */
export function mergeActionResult(
  snapshot: DriverRouteToday,
  result: DriverActionResult,
): DriverRouteToday {
  let next = snapshot;

  if (result.route && result.route.id === snapshot.route.id) {
    const server = result.route;
    next = {
      ...next,
      route: {
        ...next.route,
        status: server.status === 'completed' ? 'completed' : next.route.status,
        closedAt: next.route.closedAt ?? server.closedAt,
        actualStartAt: next.route.actualStartAt ?? server.actualStartAt,
        actualEndAt: next.route.actualEndAt ?? server.actualEndAt,
      },
    };
  }

  if (result.stop) {
    const server = result.stop;
    next = withStops(
      next,
      next.stops.map((local) => {
        if (local.id !== server.id) return local;
        const keepLocalStatus = STOP_RANK[local.status] > STOP_RANK[server.status];
        return {
          ...server,
          status: keepLocalStatus ? local.status : server.status,
          arrivedAt: local.arrivedAt ?? server.arrivedAt,
          deliveredAt: keepLocalStatus ? local.deliveredAt : (server.deliveredAt ?? local.deliveredAt),
          parcels: server.parcels.map((serverParcel) => {
            const localParcel = local.parcels.find(
              (p) => parcelTail(p.parcelNumber) === parcelTail(serverParcel.parcelNumber),
            );
            if (!localParcel) return serverParcel;
            return PARCEL_RANK[localParcel.status] > PARCEL_RANK[serverParcel.status]
              ? { ...serverParcel, status: localParcel.status, loaded: localParcel.loaded }
              : serverParcel;
          }),
        };
      }),
    );
  }

  // A-04: de daadwerkelijk geplande herbezorgdag staat ook los in de uitkomst.
  if (result.redeliveryPlannedFor && result.stop) {
    const stopId = result.stop.id;
    next = withStops(
      next,
      next.stops.map((stop) =>
        stop.id === stopId && !stop.redeliveryPlannedFor
          ? { ...stop, redeliveryPlannedFor: result.redeliveryPlannedFor ?? null }
          : stop,
      ),
    );
  }

  if (result.parcel) {
    const server = result.parcel;
    next = mapParcelByNumber(next, server.parcelNumber, (local) =>
      PARCEL_RANK[local.status] > PARCEL_RANK[server.status] ? local : server,
    );
  }

  return next;
}
